/* BP-010 — Wholetail Intelligence tab.

   Light-rehab retail resale strategy.
   Layout:
     • Inputs grid   — acquisition + light-rehab + holding + selling
     • Hero KPI      — Projected Net Profit · Wholetail Score
     • Metrics grid  — ROI · Margin · Max Purchase · Break-even
     • Score factors — 5-factor breakdown w/ progress bars
     • Risk flags
     • AI panel      — strategy recommendation + offer range
     • Saved analyses history
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import WholetailFormulaCard from "../WholetailFormulaCard";
import {
  Loader2, RotateCcw, Save, Sparkles, Download, Tag,
} from "lucide-react";

const INP = "w-full bg-white border border-slate-200 rounded-md px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100";

const EMPTY_INPUTS = {
  purchase_price: "", as_is_value: "", resale_value: "",
  light_rehab_cost: "0", repair_contingency_rate: "",
  acquisition_closing_costs: "",
  holding_months: "", monthly_holding_cost: "0",
  financing_cost: "",
  listing_commission_rate: "", seller_concessions: "0",
  resale_closing_cost_rate: "", marketing_prep_cost: "0",
  target_profit: "", target_roi: "",
};

export default function WholetailTab({ property }) {
  const [inputs, setInputs] = useState({
    ...EMPTY_INPUTS,
    purchase_price: property.asking_price || "",
    resale_value: property.published_arv || property.estimated_arv || "",
    light_rehab_cost: property.published_repair_estimate?.total || "0",
  });
  const [scenarioName, setScenarioName] = useState("Conservative Wholetail");
  const [current, setCurrent] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/properties/${property.id}/wholetail`);
      setAnalyses(data.items || []);
      if (data.items?.[0] && !current) {
        setCurrent(data.items[0]);
        setInputs({ ...EMPTY_INPUTS, ...data.items[0].inputs });
        setScenarioName(data.items[0].scenario_name);
      }
    } catch { toast.error("Failed to load Wholetail"); }
    finally { setLoading(false); }
  }, [property.id, current]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setInputs((s) => ({ ...s, [k]: e.target.value }));

  const calc = useCallback(async (save) => {
    if (!inputs.purchase_price || !inputs.resale_value) {
      toast.error("Purchase Price and Resale Value are required.");
      return;
    }
    setCalculating(true);
    try {
      const { data } = await api.post(
        `/v3/properties/${property.id}/wholetail/calculate`,
        { inputs, save, scenario_name: scenarioName },
      );
      if (save) {
        setCurrent(data);
        toast.success(`${data.scenario_name} saved`);
        load();
      } else {
        setCurrent({ inputs, outputs: data.outputs, scenario_name: scenarioName });
        toast.success("Calculated");
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Calculation failed"); }
    finally { setCalculating(false); }
  }, [inputs, property.id, scenarioName, load]);

  const runAi = useCallback(async () => {
    if (!current?.id) { toast.error("Save the analysis first."); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post(`/v3/wholetail/${current.id}/ai-review`);
      setCurrent(data);
      toast.success("AI review ready");
    } catch { toast.error("AI review failed"); }
    finally { setAiLoading(false); }
  }, [current]);

  const exportPdf = useCallback(() => {
    if (!current?.id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/v3/wholetail/${current.id}/export/pdf`, "_blank");
  }, [current]);

  const outs = useMemo(() => current?.outputs || {}, [current]);
  const score = outs.wholetail_score ? parseInt(String(outs.wholetail_score), 10) : null;
  const scoreBand = score == null ? null
    : score >= 90 ? { color: "text-emerald-700", dot: "bg-emerald-500", label: "EXCELLENT" }
    : score >= 80 ? { color: "text-emerald-700", dot: "bg-emerald-500", label: "STRONG" }
    : score >= 70 ? { color: "text-lime-700",    dot: "bg-lime-500",    label: "VIABLE" }
    : score >= 60 ? { color: "text-amber-700",   dot: "bg-amber-500",   label: "MARGINAL" }
    :                { color: "text-red-700",    dot: "bg-red-500",     label: "ALTERNATIVE" };

  if (loading) return (
    <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="wt-loading">
      <Loader2 size={14} className="animate-spin"/> Loading…
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-panel-wholetail">
      {/* Golden Wholetail MAO snapshot — single source of truth pulled
          from `/api/v3/properties/:id/metrics`. Sits on top so operators
          see the canonical number before running the deep analysis. */}
      <WholetailFormulaCard propertyId={property?.id} />
      {/* Inputs */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-500 mb-3">
          <Tag size={12}/> Light Rehab · MLS Retail Resale
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <F label="Purchase Price *"><input value={inputs.purchase_price} onChange={set("purchase_price")} data-testid="wt-purchase" className={INP} type="number"/></F>
          <F label="Resale Value (ARV) *"><input value={inputs.resale_value} onChange={set("resale_value")} data-testid="wt-resale" className={INP} type="number"/></F>
          <F label="Light Rehab Cost *"><input value={inputs.light_rehab_cost} onChange={set("light_rehab_cost")} data-testid="wt-rehab" className={INP} type="number"/></F>

          <F label="Repair Contingency (0.10 = 10%)"><input value={inputs.repair_contingency_rate} onChange={set("repair_contingency_rate")} className={INP} type="number" step="0.01" placeholder="0.10"/></F>
          <F label="Acquisition Closing (blank → 2.5%)"><input value={inputs.acquisition_closing_costs} onChange={set("acquisition_closing_costs")} className={INP} type="number" placeholder="auto"/></F>
          <F label="Financing Cost (blank → 3% of purchase)"><input value={inputs.financing_cost} onChange={set("financing_cost")} className={INP} type="number" placeholder="auto"/></F>

          <F label="Holding Months"><input value={inputs.holding_months} onChange={set("holding_months")} className={INP} type="number" placeholder="3"/></F>
          <F label="Monthly Holding Cost"><input value={inputs.monthly_holding_cost} onChange={set("monthly_holding_cost")} className={INP} type="number"/></F>
          <F label="Marketing / Prep"><input value={inputs.marketing_prep_cost} onChange={set("marketing_prep_cost")} className={INP} type="number"/></F>

          <F label="Listing Commission Rate (0.05 = 5%)"><input value={inputs.listing_commission_rate} onChange={set("listing_commission_rate")} className={INP} type="number" step="0.001" placeholder="0.05"/></F>
          <F label="Resale Closing Rate (0.02 = 2%)"><input value={inputs.resale_closing_cost_rate} onChange={set("resale_closing_cost_rate")} className={INP} type="number" step="0.001" placeholder="0.02"/></F>
          <F label="Seller Concessions"><input value={inputs.seller_concessions} onChange={set("seller_concessions")} className={INP} type="number"/></F>

          <F label="Target Profit"><input value={inputs.target_profit} onChange={set("target_profit")} className={INP} type="number" placeholder="20000"/></F>
          <F label="Target ROI (0.20 = 20%)"><input value={inputs.target_roi} onChange={set("target_roi")} className={INP} type="number" step="0.01" placeholder="0.20"/></F>
          <F label="Scenario Name"><input value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} data-testid="wt-name" className={INP}/></F>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => calc(false)} disabled={calculating} data-testid="wt-calc"
                  className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-xs font-semibold px-3 py-1.5 hover:bg-slate-700 disabled:opacity-50">
            {calculating ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Calculate
          </button>
          <button onClick={() => calc(true)} disabled={calculating} data-testid="wt-save"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-emerald-500 disabled:opacity-50">
            <Save size={12}/> Save
          </button>
          <button onClick={runAi} disabled={aiLoading || !current?.id} data-testid="wt-ai"
                  className="inline-flex items-center gap-1 rounded-md border border-purple-300 bg-purple-50 text-purple-800 text-xs font-semibold px-3 py-1.5 hover:border-purple-500 disabled:opacity-40">
            {aiLoading ? <Loader2 size={12} className="animate-spin"/> : <Sparkles size={12}/>}
            AI Review
          </button>
          <button onClick={exportPdf} disabled={!current?.id} data-testid="wt-pdf"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white text-slate-800 text-xs font-semibold px-3 py-1.5 hover:border-slate-400 disabled:opacity-40">
            <Download size={12}/> PDF
          </button>
        </div>
      </div>

      {/* Hero KPI */}
      {current && (
        <>
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-5" data-testid="wt-results">
            <div className="flex flex-wrap items-start justify-between gap-5">
              <div className="min-w-[240px]">
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Projected Net Profit</div>
                <div className={`text-4xl font-bold tabular-nums mt-1 ${parseFloat(outs.projected_net_profit) < 0 ? "text-red-700" : "text-slate-900"}`}
                     data-testid="wt-profit">
                  ${fmt(outs.projected_net_profit)}
                </div>
                <div className="text-xs text-slate-700 mt-1">
                  ROI <b>{pct(outs.roi)}</b> · Margin <b>{pct(outs.profit_margin)}</b> · Max Purchase <b>${fmt(outs.maximum_purchase_price)}</b>
                </div>
                <div className="text-[11px] text-slate-600 mt-1">
                  Break-even resale <b>${fmt(outs.break_even_resale_price)}</b> · Net proceeds <b>${fmt(outs.net_sale_proceeds)}</b>
                </div>
              </div>

              <div className="flex flex-col items-center min-w-[140px]">
                <div className="text-[10px] uppercase tracking-wider text-amber-800/80">Wholetail Score</div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className={`text-5xl font-bold tabular-nums ${scoreBand?.color || "text-slate-400"}`}
                        data-testid="wt-score">{score ?? "—"}</span>
                  <span className="text-sm text-slate-500 font-medium">/100</span>
                </div>
                {scoreBand && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider ${scoreBand.color}`}>
                    <span className={`w-2 h-2 rounded-full ${scoreBand.dot}`}/> {scoreBand.label}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Cost breakdown */}
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-4">
            <Metric label="Total Improvement" value={`$${fmt(outs.total_improvement_cost)}`}/>
            <Metric label="Total Holding" value={`$${fmt(outs.total_holding_cost)}`}/>
            <Metric label="Total Selling" value={`$${fmt(outs.total_selling_costs)}`}/>
            <Metric label="Total Project" value={`$${fmt(outs.total_project_cost)}`}/>
            <Metric label="Cash Invested" value={`$${fmt(outs.cash_invested)}`}/>
            <Metric label="Listing Commission" value={`$${fmt(outs.listing_commission)}`}/>
            <Metric label="Resale Closing" value={`$${fmt(outs.resale_closing_costs)}`}/>
            <Metric label="Repair Contingency" value={`$${fmt(outs.repair_contingency)}`}/>
          </div>

          {/* Score factor breakdown */}
          {outs.score_factors && (
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="wt-score-factors">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-3">Score Factor Breakdown</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                {[
                  { key: "profitability",        label: "Profitability",       weight: 30 },
                  { key: "capital_efficiency",   label: "Capital Efficiency",  weight: 20 },
                  { key: "marketability",        label: "Marketability",       weight: 20 },
                  { key: "execution_risk",       label: "Execution Risk",      weight: 15 },
                  { key: "value_add_efficiency", label: "Value-Add Efficiency",weight: 15 },
                ].map(({ key, label, weight }) => {
                  const factor = parseFloat(outs.score_factors[key] || 0);
                  const contribution = (factor * weight).toFixed(0);
                  return (
                    <div key={key}>
                      <div className="flex items-baseline justify-between text-xs">
                        <span className="text-slate-800 font-medium">{label}</span>
                        <span className="text-slate-500 tabular-nums text-[10px]">{contribution}/{weight}</span>
                      </div>
                      <div className="h-1.5 mt-1 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full"
                             style={{ width: `${factor * 100}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Risk flags */}
          {(outs.risk_flags || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-1" data-testid="wt-flags">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Risk Flags</div>
              {outs.risk_flags.map((w, i) => (
                <div key={i} className="text-[11px] text-slate-800">
                  <span className={`text-[9px] px-1 py-0.5 rounded uppercase font-semibold mr-2 ${
                    w.severity === "critical" ? "bg-red-100 text-red-700"
                    : w.severity === "high"   ? "bg-orange-100 text-orange-700"
                    :                            "bg-amber-100 text-amber-700"
                  }`}>{w.severity}</span>
                  {w.message}
                </div>
              ))}
            </div>
          )}

          {/* AI review */}
          {current.ai_review && (
            <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-1" data-testid="wt-ai-block">
              <div className="text-[10px] uppercase tracking-wider text-purple-700">AI Recommendation</div>
              <div className="text-sm text-slate-900">{current.ai_review.summary}</div>
              {current.ai_review.recommendedStrategy && (
                <div className="text-[11px] text-slate-700">
                  <b>Strategy:</b> {current.ai_review.recommendedStrategy}
                  {current.ai_review.confidence != null && (
                    <> · {Math.round(current.ai_review.confidence * 100)}% confidence</>
                  )}
                </div>
              )}
              {(current.ai_review.recommendedOfferLow || current.ai_review.recommendedOfferHigh) && (
                <div className="text-[11px] text-slate-700">
                  <b>Offer range:</b> ${fmt(current.ai_review.recommendedOfferLow)} – ${fmt(current.ai_review.recommendedOfferHigh)}
                </div>
              )}
              {current.ai_review.strengths?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Strengths:</b> {current.ai_review.strengths.join("; ")}</div>
              )}
              {current.ai_review.risks?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Risks:</b> {current.ai_review.risks.join("; ")}</div>
              )}
              {current.ai_review.nextActions?.length > 0 && (
                <div className="text-[11px] text-slate-700"><b>Next actions:</b> {current.ai_review.nextActions.join("; ")}</div>
              )}
            </div>
          )}
        </>
      )}

      {/* History */}
      {analyses.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="wt-history">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Saved Analyses ({analyses.length})</div>
          <div className="space-y-1">
            {analyses.map((a) => (
              <button key={a.id}
                      onClick={() => { setCurrent(a); setInputs({ ...EMPTY_INPUTS, ...a.inputs }); }}
                      data-testid={`wt-history-${a.id.slice(0,8)}`}
                      className="w-full text-left flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-slate-50">
                <span>v{a.version} · {a.scenario_name}</span>
                <span className="tabular-nums text-slate-600">
                  Score {a.outputs?.wholetail_score || "—"} · Profit ${fmt(a.outputs?.projected_net_profit)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </label>
);
const Metric = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-base font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
  </div>
);

function fmt(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function pct(v) {
  if (v == null || v === "") return "—";
  const n = parseFloat(String(v));
  return Number.isNaN(n) ? String(v) : `${(n * 100).toFixed(1)}%`;
}
